import type React from 'react';
import { lazy, memo, Suspense, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { cx } from '@/styled-system/css';
import type { ListingDetails } from 'app/types/listingDetails.type';
import { ShareButton } from 'app/shared/halo/components/features/listings/SocialActions';
import { useContactFormAction } from 'app/shared/halo/components/features/listings/ContactForm/useContactFormAction';
import { useShareAction } from 'app/shared/hooks/useShareAction';
import {
  emitTrackSaveHomeClickEvent,
  emitTrackUnSaveHomeClickEvent,
} from 'app/shared/models/Clickstream/HdpClickstreamEvents';
import { emitTrackClicksIntoPropertyEvent } from 'app/shared/models/Clickstream/ListingCardClickstreamEvents';

import {
  ChevronLeftIcon,
  ChevronRightIcon,
  ContactIcon,
  ExpandIcon,
  HeartFilledIcon,
  HeartOutlineIcon,
} from 'app/shared/halo/components/ui/icons';
import TotalMonthlyPriceBadge from 'app/shared/modules/TotalMonthlyPrice/TotalMonthlyPriceBadge';
import FullscreenOverlay from 'app/shared/halo/components/ui/FullscreenOverlay';
import { ListingDataProvider } from 'app/shared/halo/components/features/listings/NeighborhoodModule/context';
import { buildResponsiveImageAttrs, getListingPhotoUrls } from 'app/shared/utils/fedPhotos';
import noPhoto from 'images/noPhoto.png';
import api from 'app/shared/utils/api';
import ListingEngineActions from 'app/shared/flux/actions/ListingEngineActions';
import RouteActions from 'app/shared/flux/actions/RouteActions';
import UserItemActions from 'app/shared/flux/actions/UserItemActions';
import { listingUtils_checkFavorite } from 'app/shared/utils/listingUtils';
import { getListingIdentifier } from 'app/shared/utils/listingsReducerUtils';
import type { RootReduxState } from 'app/shared/flux/reducers/index';
import stringHelper from 'app/shared/utils/stringHelper';
import StringConstants from 'app/shared/constants/StringConstants';
import { triggerCannonConfetti } from 'app/shared/utils/confetti';
import * as styles from './feedCard.styles';

// P1/P2/P3: Lazy-load heavy components only used on interaction
const MiniMap = lazy(() => import('app/shared/halo/components/ui/MiniMap').then((m) => ({ default: m.MiniMap })));
const NeighborhoodSpoke = lazy(() =>
  import('app/shared/halo/components/features/listings/NeighborhoodModule/NeighborhoodSpoke').then((m) => ({
    default: m.NeighborhoodSpoke,
  })),
);
// Runtime extras the card reads that are not declared on ListingDetails.
// Some FeedView callers pass a flattened SummaryV2-shaped object that exposes
// roommatesCount / roomSqFt at the top level (vs. ListingDetails.roomForRentInfo),
// and a `propertyName` / `uri` alongside displayName / uriV2.
type FeedCardListing = ListingDetails & {
  propertyName?: string;
  roommatesCount?: string;
  roomSqFt?: number;
  uri?: string;
};

type FeedCardProps = {
  listing: FeedCardListing;
  onBeforeNavigate?: () => void;
  isActive?: boolean;
};

const MAX_VISIBLE = 7;

const getRenderedDots = (total: number, active: number): Array<number> => {
  if (total <= MAX_VISIBLE) {
    return Array.from({ length: total }, (_, i) => i);
  }
  const half = Math.floor(MAX_VISIBLE / 2);
  let start = active - half;
  let end = active + half;
  if (start < 0) {
    start = 0;
    end = MAX_VISIBLE - 1;
  }
  if (end >= total) {
    end = total - 1;
    start = total - MAX_VISIBLE;
  }
  const renderStart = Math.max(0, start - 1);
  const renderEnd = Math.min(total - 1, end + 1);
  return Array.from({ length: renderEnd - renderStart + 1 }, (_, i) => renderStart + i);
};

const getDotStyle = (index: number, active: number, total: number) => {
  if (total <= MAX_VISIBLE) {
    const dist = Math.abs(index - active);
    return {
      width: dist === 0 ? 8 : dist === 1 ? 6 : dist === 2 ? 5 : 4,
      height: dist === 0 ? 8 : dist === 1 ? 6 : dist === 2 ? 5 : 4,
      opacity: 1,
      backgroundColor: dist === 0 ? '#fff' : undefined,
      margin: '0 2px',
    };
  }

  const half = Math.floor(MAX_VISIBLE / 2);
  let windowStart = active - half;
  let windowEnd = active + half;
  if (windowStart < 0) {
    windowStart = 0;
    windowEnd = MAX_VISIBLE - 1;
  }
  if (windowEnd >= total) {
    windowEnd = total - 1;
    windowStart = total - MAX_VISIBLE;
  }

  if (index < windowStart || index > windowEnd) {
    return { width: 0, height: 0, opacity: 0, margin: '0' };
  }

  const dist = Math.abs(index - active);
  const size = dist === 0 ? 8 : dist === 1 ? 6 : dist === 2 ? 5 : 4;
  return {
    width: size,
    height: size,
    opacity: dist <= 2 ? 1 : 0.5,
    backgroundColor: dist === 0 ? '#fff' : undefined,
    margin: '0 2px',
  };
};

export const FeedCard = memo(function FeedCard({ listing, onBeforeNavigate, isActive }: FeedCardProps) {
  const dispatch = useDispatch();
  const isMobile = useSelector((state: RootReduxState) => state.app.device.screenWidth === 'sm');
  const googleMapsKey = useSelector((state: RootReduxState) => state.app.googleMaps?.key);
  const [imageLoaded, setImageLoaded] = useState(false);
  const { openContactForm, contactFormOverlay } = useContactFormAction(
    listing,
    'search_results_feed_halo',
    'property_card',
  );
  const openShare = useShareAction({
    listing,
    triggerLocation: 'search_results_feed_halo',
    triggerObject: 'property_card',
  });
  const favoriteButtonRef = useRef<HTMLDivElement>(null);
  const isLoggedIn = useSelector((state: RootReduxState) => Boolean(state.user.loggedIn || state.user.info?.email));
  const listingId = getListingIdentifier(listing);
  const listingSaved = useSelector((state: RootReduxState) => {
    const found = state.listings.listingGroups.byCoords?.find((l) => getListingIdentifier(l) === listingId);
    return listingUtils_checkFavorite(found ?? listing);
  });

  const [currentIndex, setCurrentIndex] = useState(0);
  const carouselRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const isScrollingRef = useRef(false);
  const pointerDownPosRef = useRef<{ x: number; y: number } | null>(null);

  // P6: Memoize all derived listing data
  const {
    allPhotoUrls,
    hasMultiplePhotos,
    propertyNameText,
    listingMinMaxPriceBeds,
    priceMin,
    priceMax,
    unitCount,
    roommatesCount,
    roomSqFt,
    geo,
    hasGeo,
    locationLine,
  } = useMemo(() => {
    const derivedAllPhotoUrls = getListingPhotoUrls(listing);
    const derivedPropertyNameText = listing?.propertyName || listing?.displayName || listing?.address?.street;
    const address = listing?.address;
    const city = address?.city;
    const listingState = address?.state;
    const derivedMinMaxPriceBeds = listing?.listingMinMaxPriceBeds;
    const isApartmentBldg = listing?.isApartmentBldg;
    const propertyType = listing?.propertyType;
    const derivedGeo = listing?.geo;

    const typeMap: Record<string, string> = {
      house: 'House',
      condo: 'Condo',
      townhouse: 'Townhouse',
      medium: 'Apt building',
      large: 'Apt building',
      garden: 'Apt building',
    };
    const listingTypeLabel = isApartmentBldg
      ? 'Apt building'
      : propertyType
        ? typeMap[propertyType] || propertyType.charAt(0).toUpperCase() + propertyType.slice(1)
        : null;

    const listingType = listing?.listingType;
    const roommatesCount = listing?.roommatesCount;
    const roomSqFt = listing?.roomSqFt;
    const r4rPrefix = listingType === 'room' ? StringConstants.R4R_PREFIX : '';
    return {
      allPhotoUrls: derivedAllPhotoUrls,
      hasMultiplePhotos: derivedAllPhotoUrls.length > 1,
      propertyNameText: derivedPropertyNameText,
      listingMinMaxPriceBeds: derivedMinMaxPriceBeds,
      priceMin: derivedMinMaxPriceBeds?.minPrice,
      priceMax: derivedMinMaxPriceBeds?.maxPrice,
      unitCount: listing?.modelTypeUnitCount,
      roommatesCount,
      roomSqFt,
      geo: derivedGeo,
      hasGeo: derivedGeo?.lat != null && derivedGeo?.lon != null,
      locationLine:
        r4rPrefix + [listingTypeLabel, [city, listingState].filter(Boolean).join(', ')].filter(Boolean).join(' in '),
    };
  }, [listing]);

  const needsStreetView = allPhotoUrls.length === 0 && hasGeo && googleMapsKey;
  const [streetViewUrl, setStreetViewUrl] = useState<string | null>(null);
  const [streetViewFailed, setStreetViewFailed] = useState(false);
  useEffect(() => {
    if (!needsStreetView) {
      setStreetViewUrl(null);
      setStreetViewFailed(false);
      return;
    }
    setStreetViewFailed(false);
    const unsignedUrl = `https://maps.googleapis.com/maps/api/streetview?size=640x480&location=${geo!.lat},${geo!.lon}`;
    if (process.env.NODE_ENV === 'development') {
      setStreetViewUrl(`${unsignedUrl}&key=${googleMapsKey}`);
      return;
    }
    let cancelled = false;
    const signPromise = dispatch(api.area.signUrl(unsignedUrl)) as unknown as Promise<{
      data: { signedUrl: string };
    }>;
    signPromise
      .then((res) => {
        if (!cancelled) {
          setStreetViewUrl(res.data.signedUrl);
        }
        return undefined;
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
  }, [needsStreetView, geo, googleMapsKey, dispatch]);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || !hasMultiplePhotos) return;

    let rafId: number;
    const handleScroll = () => {
      cancelAnimationFrame(rafId);
      rafId = requestAnimationFrame(() => {
        if (isScrollingRef.current) return;
        const index = Math.round(track.scrollLeft / track.clientWidth);
        setCurrentIndex(index);
      });
    };

    track.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      track.removeEventListener('scroll', handleScroll);
      cancelAnimationFrame(rafId);
    };
  }, [hasMultiplePhotos]);

  const goToPrev = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;
    track.scrollTo({ left: Math.max(0, currentIndex - 1) * track.clientWidth, behavior: 'smooth' });
  }, [currentIndex]);

  const goToNext = useCallback(() => {
    const track = trackRef.current;
    if (!track || !allPhotoUrls) return;
    track.scrollTo({
      left: Math.min(allPhotoUrls.length - 1, currentIndex + 1) * track.clientWidth,
      behavior: 'smooth',
    });
  }, [currentIndex, allPhotoUrls]);

  useEffect(() => {
    const el = carouselRef.current;
    if (!el || !hasMultiplePhotos || !isActive) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrev();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNext();
      }
    };

    el.addEventListener('keydown', handleKeyDown);
    return () => el.removeEventListener('keydown', handleKeyDown);
  }, [hasMultiplePhotos, isActive, goToPrev, goToNext]);

  useEffect(() => {
    if (hasMultiplePhotos && isActive) {
      const id = requestAnimationFrame(() => {
        carouselRef.current?.focus({ preventScroll: true });
      });
      return () => cancelAnimationFrame(id);
    }
  }, [hasMultiplePhotos, isActive]);

  const listingUri = listing?.uriV2 || listing?.uri;
  // P9: Memoize to avoid dispatching during every render
  const listingPath = useMemo(
    () => (listingUri ? (dispatch(RouteActions.buildListingPath({ listingUri })) as unknown as string) : null),
    [dispatch, listingUri],
  );

  const handlePointerDown = useCallback((e: React.PointerEvent) => {
    pointerDownPosRef.current = { x: e.clientX, y: e.clientY };
  }, []);

  const handleCarouselTap = useCallback(
    (e: React.MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('button') || target.closest('a')) return;

      const down = pointerDownPosRef.current;
      if (!down) return;

      const TAP_TOLERANCE = 10;
      const dx = Math.abs(e.clientX - down.x);
      const dy = Math.abs(e.clientY - down.y);
      if (dx > TAP_TOLERANCE || dy > TAP_TOLERANCE) return;

      emitTrackClicksIntoPropertyEvent({
        listing,
        triggerLocation: 'search_results_feed_halo',
      });
      onBeforeNavigate?.();
      if (listingPath) {
        dispatch(ListingEngineActions.clearCurrentListing());
        window.router.transitionTo(listingPath);
      }
    },
    [dispatch, listing, listingPath, onBeforeNavigate],
  );

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const handleFavoriteClick = () => {
    if (!listingSaved && isLoggedIn) {
      const prefersReducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      if (!prefersReducedMotion) {
        const rect = favoriteButtonRef.current?.getBoundingClientRect();
        if (rect) {
          triggerCannonConfetti({
            origin: {
              x: rect.left + rect.width / 2,
              y: rect.top + rect.height / 2,
            },
          });
        }
      }
    }
    if (listingSaved) {
      emitTrackUnSaveHomeClickEvent({
        listing,
        triggerLocation: 'search_results_feed_halo',
        triggerObject: 'property_card',
      });
      dispatch(UserItemActions.removeUserItem('favorite', listing));
    } else {
      emitTrackSaveHomeClickEvent({
        listing,
        triggerLocation: 'search_results_feed_halo',
        triggerObject: 'property_card',
      });
      dispatch(UserItemActions.addUserItem('favorite', listing));
    }
  };

  const handleViewDetails = (e: React.MouseEvent) => {
    emitTrackClicksIntoPropertyEvent({
      listing,
      triggerLocation: 'search_results_feed_halo',
    });
    if (e.metaKey || e.ctrlKey || e.button === 1) return;

    e.preventDefault();
    onBeforeNavigate?.();
    if (listingPath) {
      dispatch(ListingEngineActions.clearCurrentListing());
      window.router.transitionTo(listingPath);
    }
  };

  return (
    <>
      <article data-testid="feed-card" className={styles.feedCard}>
        {hasGeo && isMobile && (
          <Suspense fallback={null}>
            <FullscreenOverlay.Root defaultOpen={false}>
              <FullscreenOverlay.Trigger asChild>
                <button type="button" className={styles.miniMapTrigger} aria-label="View neighborhood map">
                  <MiniMap lat={geo!.lat as number} lon={geo!.lon as number} size={54} />
                </button>
              </FullscreenOverlay.Trigger>
              <FullscreenOverlay.Portal>
                <FullscreenOverlay.Backdrop>
                  <FullscreenOverlay.Content>
                    <Suspense fallback={null}>
                      <ListingDataProvider listing={listing}>
                        <NeighborhoodSpoke />
                      </ListingDataProvider>
                    </Suspense>
                  </FullscreenOverlay.Content>
                </FullscreenOverlay.Backdrop>
              </FullscreenOverlay.Portal>
            </FullscreenOverlay.Root>
          </Suspense>
        )}
        <div
          ref={carouselRef}
          className={styles.imageContainer}
          onPointerDown={handlePointerDown}
          onClick={handleCarouselTap}
          role={hasMultiplePhotos ? 'group' : undefined}
          aria-roledescription={hasMultiplePhotos ? 'carousel' : undefined}
          aria-label={hasMultiplePhotos ? 'Property photos' : undefined}
          tabIndex={hasMultiplePhotos ? 0 : undefined}
        >
          {allPhotoUrls.length > 0 ? (
            hasMultiplePhotos ? (
              <div ref={trackRef} className={styles.carouselTrack}>
                {allPhotoUrls.map((url, i) => {
                  const nearby = Math.abs(i - currentIndex) <= 2;

                  if (!nearby) {
                    return <div key={url} className={styles.carouselSlide} />;
                  }

                  const attrs = buildResponsiveImageAttrs(url, 1152, [768, 1152, 1536]);

                  return (
                    <div key={url} className={styles.carouselSlide}>
                      <img
                        src={attrs?.src || url}
                        srcSet={attrs?.srcSet}
                        sizes="100vw"
                        alt={`${propertyNameText || 'Property'} - photo ${i + 1}`}
                        className={cx(
                          styles.carouselImage,
                          imageLoaded && styles.imageLoaded,
                          i === 0 && imageLoaded && currentIndex === 0 && styles.imagePanning,
                        )}
                        onLoad={i === 0 ? handleImageLoad : undefined}
                        loading={i === currentIndex ? 'eager' : 'lazy'}
                        draggable={false}
                      />
                    </div>
                  );
                })}
              </div>
            ) : (
              (() => {
                const singleUrl = allPhotoUrls[0];
                const imageAttrs = buildResponsiveImageAttrs(singleUrl, 1152, [768, 1152, 1536]);
                return (
                  <img
                    src={imageAttrs?.src ?? singleUrl}
                    srcSet={imageAttrs?.srcSet}
                    sizes="100vw"
                    alt={`${propertyNameText || 'Property'} photo`}
                    className={cx(
                      styles.carouselImage,
                      imageLoaded && styles.imageLoaded,
                      imageLoaded && styles.imagePanning,
                    )}
                    onLoad={handleImageLoad}
                    draggable={false}
                  />
                );
              })()
            )
          ) : streetViewUrl && !streetViewFailed ? (
            <img
              src={streetViewUrl}
              alt={`Street view near ${propertyNameText || 'property'}`}
              className={styles.carouselImage}
              onError={() => setStreetViewFailed(true)}
              draggable={false}
            />
          ) : (
            <img src={noPhoto} alt="" className={styles.carouselImage} draggable={false} />
          )}
          {hasMultiplePhotos && (
            <>
              <button
                type="button"
                data-carousel-arrow
                className={cx(styles.arrowButton, styles.arrowButtonPrev)}
                onClick={(e) => {
                  e.stopPropagation();
                  goToPrev();
                }}
                disabled={currentIndex === 0}
                aria-label="Previous photo"
              >
                <ChevronLeftIcon />
              </button>
              <button
                type="button"
                data-carousel-arrow
                className={cx(styles.arrowButton, styles.arrowButtonNext)}
                onClick={(e) => {
                  e.stopPropagation();
                  goToNext();
                }}
                disabled={currentIndex === (allPhotoUrls?.length ?? 1) - 1}
                aria-label="Next photo"
              >
                <ChevronRightIcon />
              </button>
            </>
          )}
        </div>

        <div className={styles.bottomOverlay}>
          <div className={styles.bottomColumns}>
            <div className={styles.bottomLeftColumn}>
              <h2 className={styles.propertyName}>{propertyNameText || 'Property Name'}</h2>
              {locationLine && <div className={styles.listingType}>{locationLine}</div>}

              {priceMin != null && priceMax != null && (
                <div className={styles.priceContainer}>
                  {priceMin === 0 && priceMax === 0 ? (
                    <div className={styles.priceDisplay}>Call for price</div>
                  ) : (
                    <>
                      <div className={styles.priceDisplay}>
                        {priceMin === priceMax
                          ? `$${priceMin.toLocaleString()}/mo`
                          : `$${priceMin.toLocaleString()} - $${priceMax.toLocaleString()}/mo`}
                      </div>
                      <span className={styles.interactiveInline}>
                        <TotalMonthlyPriceBadge listing={listing} iconVariant="white" size="sm" />
                      </span>
                    </>
                  )}
                </div>
              )}

              <div className={styles.detailsText}>
                {listingMinMaxPriceBeds?.bedDisplaySemiCompact && (
                  <span>
                    {listingMinMaxPriceBeds.bedDisplaySemiCompact}
                    {listingMinMaxPriceBeds.bathDisplaySemiCompact && (
                      <span>, {listingMinMaxPriceBeds.bathDisplaySemiCompact}</span>
                    )}
                    {unitCount &&
                    listing?.listingType !== 'room' &&
                    listing?.listingType !== 'sublet' &&
                    !listing?.isUnit
                      ? ` | ${stringHelper.pluralize(unitCount, 'unit', 'units')} available`
                      : ''}
                    {roommatesCount
                      ? ` | ${roommatesCount} ${roommatesCount === '1' ? 'housemate' : 'housemates'}`
                      : ''}
                    {roomSqFt ? `, ${roomSqFt} sqft room` : ''}
                  </span>
                )}
              </div>

              <a
                className={styles.expandButton}
                href={listingPath || '#'}
                onClick={handleViewDetails}
                aria-label="View full property details"
              >
                View full property details
                <ExpandIcon />
              </a>
            </div>

            <div data-testid="feed-card-actions" className={styles.rightActions}>
              <div ref={favoriteButtonRef} className={styles.actionWrapper}>
                <button
                  className={cx(styles.saveButton, listingSaved ? styles.saveButtonSaved : styles.saveButtonUnsaved)}
                  onClick={handleFavoriteClick}
                  aria-label={listingSaved ? 'Remove from saved listings' : 'Save this listing'}
                >
                  {listingSaved ? <HeartFilledIcon /> : <HeartOutlineIcon />}
                </button>
                <div className={styles.actionLabel}>{listingSaved ? 'Saved' : 'Save'}</div>
              </div>
              <div className={styles.actionWrapper}>
                <button className={styles.actionButton} onClick={openContactForm} aria-label="Contact property">
                  <ContactIcon />
                </button>
                <div className={styles.actionLabel}>Contact</div>
              </div>
              <div className={styles.actionWrapper}>
                <ShareButton onClick={openShare} aria-label="Share this listing" />
                <div className={styles.actionLabel}>Share</div>
              </div>
            </div>
          </div>

          {hasMultiplePhotos &&
            allPhotoUrls &&
            (() => {
              const total = allPhotoUrls.length;
              const rendered = getRenderedDots(total, currentIndex);
              return (
                <div data-testid="pagination-dots" className={styles.paginationContainer} aria-hidden="true">
                  {rendered.map((dotIndex) => (
                    <div
                      key={dotIndex}
                      className={styles.paginationDot}
                      style={getDotStyle(dotIndex, currentIndex, total)}
                    />
                  ))}
                </div>
              );
            })()}
          {hasMultiplePhotos && allPhotoUrls && (
            <div className={styles.srOnly} role="status" aria-live="polite">
              Photo {currentIndex + 1} of {allPhotoUrls.length}
            </div>
          )}
        </div>
      </article>
      {contactFormOverlay}
    </>
  );
});

export default FeedCard;
